import { CategoryDownloadDto, ContentController, ControllerHelper, FacetResult, FetchPostsFilter, FileEntrySearchFilter, SearchResultsDto, UserController, UserShortInfoDownloadDto } from "collaboration-service";
import useCategoryTypeSelection, { useCategoryTypeSelectionProps } from "components/CategorySelection/useCategoryTypeSelection";
import FacetSelection from "components/FacetSelection/FacetSelection";
import FacetSelectionMulti from "components/FacetSelection/FacetSelectionMulti";
import SidebarTitleContainer from "components/Sidebars/SidebarTitleContainer";
import { Button, Flag } from "imaginarity-react-ui";
import * as _ from "lodash";
import * as React from "react";
import { useAppSelector } from "services/ApplicationState/ApplicationState";
import { getTranslated } from "services/Helpers/TranslationHelpers";
import { useImgI18N } from "services/ImgI18N";
import { SidebarContainerTitleOneLine } from "views/CustomSC";

export type StandardFilterMoreFiltersType = (
    newFilter: FetchPostsFilter,
    setNewFilter: React.Dispatch<React.SetStateAction<FetchPostsFilter>>,
    newFeFilter: FileEntrySearchFilter | undefined,
    setNewFeFilter: React.Dispatch<React.SetStateAction<FileEntrySearchFilter | undefined>>,
) => JSX.Element | null;

export type GlobalPostSearchFilterProps = {
    filter: FetchPostsFilter;
    feFilter?: FileEntrySearchFilter;
    facets?: SearchResultsDto<any>["facets"];
    containerRef: React.RefObject<HTMLDivElement>;
    cmsMode: "Project" | "File";

    moreFilters?: StandardFilterMoreFiltersType;

    onChange: (filter: FetchPostsFilter) => void;
    onChangeFE: (filter: FileEntrySearchFilter | undefined) => void;
    onChangeMode: (mode: "Project" | "File") => void;
    hideGroupFilter?: boolean;
};

const StandardFilter = (p: GlobalPostSearchFilterProps) => {
    const { t } = useImgI18N("general");
    const { filter, onChange, facets, moreFilters, containerRef, onChangeFE, feFilter, hideGroupFilter } = p;
    const contentLanguage = useAppSelector(state => state.contentLanguage);
    //console.log(facets);
    const [selectProps, setSelectProps] = React.useState<useCategoryTypeSelectionProps["selectProps"]>({});
    const [newFilter, setNewFilter] = React.useState<FetchPostsFilter>(filter);
    const [newFeFilter, setNewFeFilter] = React.useState<FileEntrySearchFilter | undefined>(feFilter);
    const [authors, setAuthors] = React.useState<UserShortInfoDownloadDto[]>();
    const flatAdminGroups = useAppSelector(state => state.flatAdminGroups);
    const [loadedCategories, setLoadedCategories] = React.useState<CategoryDownloadDto[]>();
    const [unspecificCategories, setUnSpecificCategories] = React.useState<{ [key: string]: FacetResult[] }>();

    const changed = React.useMemo(() => {
        return !_.isEqual(newFilter, filter);
    }, [newFilter, filter]);
    const changedFe = React.useMemo(() => {
        return !_.isEqual(newFeFilter ?? {}, feFilter ?? {});
    }, [newFeFilter, feFilter]);

    React.useEffect(() => {
        setSelectProps(old => {
            const n = _.clone(old) ?? {};
            n.menuPortalTarget = containerRef.current;
            if (!_.isEqual(n, old)) {
                return n;
            }
            return old;
        })
    }, [containerRef]);


    const {
        toRender,
        selectedCategories,
        configurations
    } = useCategoryTypeSelection({
        type: "filter",
        grouptype: "CMS",
        counts: facets?.["categoryIds"],
        selectProps,
        selectedCategories: filter.categories
    })

    React.useEffect(() => {
        const ids: string[] = [];
        _.forEach(configurations?.configurations, c => {
            ids.push(..._.map(c.categories, cc => cc.id));
        });
        const catIds = _.map(facets?.["categoryIds"], c => c.data);
        const toLoad = _.difference(catIds, ids);
        if (toLoad.length > 0) {
            const l = async () => {
                const tasks = _.map(toLoad, id => ControllerHelper.singleCall({ id }, ContentController.GetCategoryById, true));
                const res = _.compact(await Promise.all(tasks));
                setLoadedCategories(_.compact(res));
                const facetsToSet = _.map(res, r => r.id);
                const unspecificCats = { categoryIds: _.filter(facets?.["categoryIds"], c => facetsToSet.includes(c.data)) };
                setUnSpecificCategories(unspecificCats);
            }
            l();
        }
        else {
            setLoadedCategories(undefined);
            setUnSpecificCategories(undefined);
        }
    }, [configurations, facets]);

    //console.log("unCoveredCatIds", loadedCategories);



    React.useEffect(() => {
        setNewFilter(nF => {
            const toRet = (selectedCategories ?? []).length === 0 ? { ...filter } : { ...filter, categories: _.map(selectedCategories, c => c.id) };
            return toRet;
        });
    }, [selectedCategories, filter]);

    const apply = React.useMemo(() => () => {
        if (changed)
            onChange(newFilter);
        if (changedFe)
            onChangeFE(_.isEqual(newFeFilter, {}) ? undefined : newFeFilter);
    }, [changed, newFilter, onChange, changedFe, newFeFilter, onChangeFE]);


    const clearFilter = React.useMemo(() => () => {
        onChange({ includedPostTypes: newFilter.includedPostTypes, });
    }, [newFilter, onChange]);

    const groups = React.useMemo(() => {
        return _.compact(_.map(facets?.["group_id"], c => flatAdminGroups?.[c.data]));
    }, [facets, flatAdminGroups]);

    React.useEffect(() => {
        const l = async () => {
            //const tasks = _.map(facets?.["creator_id"], a => ControllerHelper.singleCall({}, UserController.GetUserInfoByUserIds) a.data);
            const ids = _.map(facets?.["creator_id"], a => a.data);
            const res = await ControllerHelper.singleCall({ ids }, UserController.GetUserInfoByUserIds);
            setAuthors(res);
        }
        l();
    }, [facets]);

    //console.log("authors", authors);

    const tGroups = React.useMemo(() => (id: string) => _.find(groups, g => g.id === id)?.name ?? "", [groups]);
    const tUsers = React.useMemo(() => (id: string) => {
        const user = _.find(authors, a => a.id === id);
        return user ? t("{{firstname}} {{secondname}}", { firstname: user.firstName, secondname: user.secondName }) : "";
    }, [authors, t]);

    const tCats = React.useMemo(() => (id: string) => {
        const cat = _.find(loadedCategories, a => a.id === id);
        return getTranslated(cat?.names, contentLanguage)?.text ?? "";
    }, [loadedCategories, contentLanguage]);

    const setGroup = React.useMemo(() => (groupId: string | null) => {
        if (groupId === null || groupId === undefined || groupId === "") {
            const toSet = { ...newFilter, onlyInGroups: undefined };
            delete toSet.onlyInGroups;
            setNewFilter(toSet);
        }
        else {
            const toSet = { ...newFilter, onlyInGroups: [groupId] };
            setNewFilter(toSet);
        }
    }, [newFilter]);

    const setCats = React.useMemo(() => (catIds: string[] | null) => {
        let curCats = _.filter(_.clone(newFilter.categories ?? []), c => _.find(loadedCategories, lc => lc.id === c) === undefined);
        const nF = { ...newFilter };
        if (catIds !== null && catIds !== undefined && catIds.length > 0) {
            curCats.push(...catIds);
        }
        if (curCats.length === 0)
            delete nF.categories;
        else
            nF.categories = _.uniq(curCats);
        setNewFilter(nF);
    }, [newFilter, loadedCategories]);

    const setAuthor = React.useMemo(() => (authorId: string | null) => {
        if (authorId === null || authorId === undefined || authorId === "") {
            const toSet = { ...newFilter, linkedUserId: undefined };
            delete toSet.linkedUserId;
            setNewFilter(toSet);
        }
        else {
            const toSet = { ...newFilter, linkedUserId: authorId };
            setNewFilter(toSet);
        }
    }, [newFilter]);

    const setOrg = React.useMemo(() => (orgParam: string) => (org: string | null) => {
        if (org === null || org === undefined || org === "") {
            const toSet = { ...newFilter };
            delete (toSet as any)[orgParam];
            setNewFilter(toSet);
        }
        else {
            const toSet: FetchPostsFilter = { ...newFilter };
            (toSet as any)[orgParam] = org;
            setNewFilter(toSet);
        }
    }, [newFilter]);


    //console.log("newFilter", newFilter);

    const canApply = changed || changedFe;

    return <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", width: "100%", position: "sticky", top: 0, zIndex: 100, backgroundColor: "#F5F4F7", padding: 5 }}>
            <div>
                <Button icon="filter undo" content={"clear all filter"} onClick={clearFilter} kind="halfTransparentButton" floated="left" />
            </div>
            <div>
                <Button icon="filter" content={"apply filter"} onClick={apply} disabled={!canApply} kind={canApply ? "primary" : "halfTransparentButton"} floated="right" />
            </div>
        </div>
        <div style={{ padding: "20px 5px 100px 5px", display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "max-content", rowGap: 10, width: "100%" }}>
            {moreFilters?.(newFilter, setNewFilter, newFeFilter, setNewFeFilter)}
            {!hideGroupFilter && (facets?.["group_id"] ?? []).length > 0 &&
                <div>
                    <FacetSelection
                        facetName='group_id'
                        facets={facets}
                        //noCount
                        value={newFilter.onlyInGroups?.[0] ?? null}
                        onChange={setGroup}
                        header={<SidebarTitleContainer icon="filter" title={t("filter by group")} noIcon noBold />}
                        //iconRendererForLabels={(ctry) => <Flag name={ctry as any} size={"18px"} />}
                        translateLabels={tGroups}
                    />
                </div>
            }
            {authors && (facets?.["creator_id"] ?? []).length > 0 &&
                <div>
                    <FacetSelection
                        facetName='creator_id'
                        facets={facets}
                        //noCount
                        value={newFilter.linkedUserId ?? null}
                        //value={newFilter.onlyInGroups?.[0] ?? null}
                        onChange={setAuthor}
                        header={<SidebarTitleContainer icon="filter" title={t("filter by author")} noIcon noBold />}
                        translateLabels={tUsers}
                    />
                </div>
            }
            {(facets?.["organization2"] ?? []).length > 0 &&
                <div>
                    <FacetSelection
                        facetName='organization2'
                        facets={facets}
                        noCount
                        value={newFilter.organization2 ?? null}
                        onChange={setOrg("organization2")}
                        header={<SidebarContainerTitleOneLine style={{ paddingLeft: 6 }} >
                            {/* market */}
                            {t("filter organization2")}
                        </SidebarContainerTitleOneLine>} />
                </div>
            }
            {(facets?.["organization"] ?? []).length > 0 &&
                <div>
                    <FacetSelection
                        facetName='organization'
                        facets={facets} noCount
                        value={newFilter.organization ?? null}
                        onChange={setOrg("organization")}
                        header={<SidebarContainerTitleOneLine
                            style={{ paddingLeft: 6 }}
                        >
                            {/* country */}
                            {t("filter organization")}
                        </SidebarContainerTitleOneLine>}
                    />
                </div>
            }
            {(facets?.["organization1"] ?? []).length > 0 &&
                <div>
                    <FacetSelection
                        facetName='organization1'
                        facets={facets}
                        noCount
                        value={newFilter.organization1 ?? null}
                        onChange={setOrg("organization1")}
                        iconRendererForLabels={(ctry) => <Flag name={ctry as any} size={"18px"} />}
                        translateLabels={t}
                        header={<SidebarContainerTitleOneLine style={{ paddingLeft: 6 }} >
                            {t("filter organization1")}:
                        </SidebarContainerTitleOneLine>}
                    />
                </div>
            }
            {unspecificCategories?.["categoryIds"] && unspecificCategories["categoryIds"].length > 0 &&
                <div>
                    <FacetSelectionMulti
                        facetName='categoryIds'
                        facets={unspecificCategories}
                        //noCount
                        value={newFilter.categories ?? null}
                        onChange={setCats}
                        header={<SidebarTitleContainer icon="filter" title={t("unspecific categories")} noIcon noBold />}

                        //iconRendererForLabels={(ctry) => <Flag name={ctry as any} size={"18px"} />}
                        translateLabels={tCats}
                    />
                </div>
            }
            {_.map(toRender, r => {
                return (
                    <div key={r.configuration.categoryType}>
                        {/* <CMSSC.Label style={{ paddingRight: 10, paddingTop: 10 }}>
                            {getTranslated(r.configuration.names)?.text ?? ""}
                        </CMSSC.Label> */}
                        <SidebarTitleContainer icon="filter" title={getTranslated(r.configuration.names)?.text ?? ""} noIcon noBold />
                        <div >{r.Select}</div>
                        <div />
                    </div>
                )
            })}
        </div>
    </>
        ;
};

export default StandardFilter;